import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import Section from '@components/Section'
import Input from '@components/Input'
import Select from '@components/Select'
import Button from '@components/Button'
import Spinner from '@components/Spinner'
import Modal from '@components/Modal'

import { sort } from '@/util/sorters'
import { useClassrooms } from '@hooks/index'
import type { Student, Classroom } from '@services/contribution/contribution'
import useStudents from '@/hooks/useStudents'

import type { StudentData } from '@/hooks/useStudents'

export interface AddOrEditStudentFormProps {
  student?: Partial<Student>
  onSubmit: (data: StudentData) => Promise<boolean>
  onDelete?: () => Promise<boolean>
}

export default function AddOrEditStudentForm({ student, onSubmit, onDelete }: AddOrEditStudentFormProps) {
  const { t } = useTranslation()
  const [firstName, setFirstName] = useState(student?.firstName ?? '')
  const [lastName, setLastName] = useState(student?.lastName ?? '')
  const [classroom, setClassroom] = useState(student?.classroom)
  const [isPending, setIsPending] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { data, error: fetchError, isFetching } = useClassrooms()
  const { students } = useStudents()
  const showDeleteButton = useMemo(() => {
    const allowDelete = onDelete && (students?.length ?? 0) > 1 ? true : false
    return allowDelete
  }, [onDelete, students])

  const otherClassroom: Classroom = {
    id: null,
    name: t('content.components.add_or_edit_student.classroom_other_option'),
    grade: t('content.components.add_or_edit_student.classroom_other_option'),
  }
  const classrooms = data ? [...data, otherClassroom] : [otherClassroom]

  const formatClassroomForDisplay = (classroom: Classroom | null) => {
    if (!classroom) return ''
    return (
      <span className='flex justify-between w-full items-center'>
        <span className='text-left flex-1'>
          {t('content.components.add_or_edit_student.classroom_grade_prefix')}
          {classroom.grade ?? ''}
        </span>
        <span className='text-right flex-1'>{classroom.name ?? ''}</span>
      </span>
    )
  }
  /*
  const sortedClassrooms: Classroom[] = data ? sort(data, 'GRADE_ASC') : []
  const classrooms = sortedClassrooms.map((classroom) => classroom.id?.toString() ?? '')

  const formatClassroomForDisplay = (option: string) => {
    const classroom = sortedClassrooms.find((c) => c.id?.toString() === option)
    if (!classroom) return option

    return (
      <span className='flex justify-between w-full items-center'>
        <span className='text-left flex-1'>{classroom.grade ?? ''}</span>
        <span className='text-right flex-1'>{classroom.name ?? ''}</span>
      </span>
    )
  }
  */

  async function handleSubmit() {
    setIsPending(true)

    //prevent creating duplicate students
    const trimmedFirst = firstName.trim().toLowerCase()
    const trimmedLast = lastName.trim().toLowerCase()
    const isDuplicate = students?.some(
      (s) =>
        s.firstName.toLowerCase() === trimmedFirst &&
        s.lastName.toLowerCase() === trimmedLast &&
        s.id !== student?.id
    )
    if (isDuplicate) {
      toast.error(t('content.components.add_or_edit_student.duplicate_error_toast'), { autoClose: false })
      setIsPending(false)
      return
    }

    const success = await onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      classroomId: classroom?.id ?? null,
    })
    if (success && !student) {
      setFirstName('')
      setLastName('')
      setClassroom(undefined)
    }

    setIsPending(false)
  }

  if (isFetching)
    return (
      <Section wrapperStyle='bg-white shadow-sm w-full max-w-4xl mx-auto' rounded={true}>
        <div className='flex justify-center items-center h-full'>
          <Spinner />
        </div>
      </Section>
    )
  if (fetchError) return <div>Error: {(fetchError as Error).message}</div>

  return (
    <div className='flex flex-col gap-6'>
      {onDelete && student?.id && (
        <Modal
          variant='danger'
          title={t('content.components.add_or_edit_student.delete_modal_title')}
          message={t('content.components.add_or_edit_student.delete_modal_body', {
            name: `${student?.firstName ?? ''} ${student?.lastName ?? ''}`.trim(),
          })}
          buttonLabel={t('content.components.add_or_edit_student.delete_modal_confirm_button')}
          show={showDeleteModal}
          onConfirm={() => onDelete()}
          onClose={() => setShowDeleteModal(false)}
        />
      )}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
        <Input
          label={t('content.components.add_or_edit_student.first_name_field_title')}
          type='text'
          value={firstName}
          placeholder={t('content.components.add_or_edit_student.first_name_field_placeholder')}
          onInput={setFirstName}
          id='student-first-name'
        />

        <Input
          label={t('content.components.add_or_edit_student.last_name_field_title')}
          type='text'
          value={lastName}
          placeholder={t('content.components.add_or_edit_student.last_name_field_placeholder')}
          onInput={setLastName}
          id='student-last-name'
        />

        <Select<Classroom>
          label={t('content.components.add_or_edit_student.classroom_field_title')}
          options={sort(classrooms, 'GRADE_ASC')}
          value={classroom}
          onSelect={setClassroom}
          formatOptionForDisplay={formatClassroomForDisplay}
        />
      </div>

      <div className='flex flex-row  justify-between pt-2 w-full mx-auto'>
        {showDeleteButton && (
          <div className='w-1/6'>
            <Button
              label={t('content.components.add_or_edit_student.delete_button')}
              onClick={() => setShowDeleteModal(true)}
              variant='danger'
              size='xl'
            />
          </div>
        )}
        <div className={`${showDeleteButton ? 'w-4/6' : 'w-full'}`}>
          <Button
            label={
              isPending
                ? ''
                : student
                  ? t('content.components.add_or_edit_student.update_button')
                  : t('content.components.add_or_edit_student.add_button')
            }
            onClick={handleSubmit}
            variant='cta'
            size='xl'
            fullwidth
            disabled={!firstName.trim() || !lastName.trim() || !classroom || isPending}
            icon={isPending ? <Spinner size='sm' /> : null}
          />
        </div>
      </div>
    </div>
  )
}
